<script lang="ts" setup>
import { ref, onMounted } from "vue"
import { useRouter } from "vue-router"
import axios from "axios"

const router = useRouter()
const password = ref("")
const confirmPassword = ref("")
const error = ref("")
const loading = ref(false)
const checking = ref(true)

onMounted(async () => {
    try {
        const res = await axios.get("/testup-auth/auth/setup-status")
        if (!res.data.needsSetup) {
            router.replace("/login")
        }
    } catch {
        // If the check fails, show the form anyway
    } finally {
        checking.value = false
    }
})

async function handleSetup() {
    error.value = ""
    if (password.value !== confirmPassword.value) {
        error.value = "Passwords do not match"
        return
    }
    if (password.value.length < 8) {
        error.value = "Password must be at least 8 characters"
        return
    }
    loading.value = true
    try {
        await axios.post("/testup-auth/auth/setup", {
            password: password.value
        })
        router.push("/login")
    } catch (e: any) {
        error.value = e.response?.data?.error || "Setup failed"
    } finally {
        loading.value = false
    }
}
</script>

<template lang="pug">
.auth-page
    .auth-card(v-if="!checking")
        img.auth-logo(src="@/assets/images/testup-icon-rahmenlos-300x220.png")
        h2 Initial Setup
        p.auth-subtitle Set a password for the super admin account
        .auth-error(v-if="error") {{ error }}
        form(@submit.prevent="handleSetup")
            .form-group
                label(for="password") Password
                input#password(
                    v-model="password"
                    type="password"
                    placeholder="At least 8 characters"
                    required
                    minlength="8"
                    autocomplete="new-password"
                )
            .form-group
                label(for="confirmPassword") Confirm Password
                input#confirmPassword(
                    v-model="confirmPassword"
                    type="password"
                    placeholder="Confirm password"
                    required
                    autocomplete="new-password"
                )
            button.auth-btn(type="submit" :disabled="loading")
                | {{ loading ? "Setting up..." : "Complete Setup" }}
</template>

<style scoped>
.auth-page {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: linear-gradient(160deg, #f8f9fa 0%, #e9ecef 100%);
}

.auth-card {
    background: white;
    padding: 48px 40px;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
    max-width: 400px;
    width: 100%;
    text-align: center;
}

.auth-logo {
    width: 60px;
    margin-bottom: 16px;
}

h2 {
    margin: 0 0 8px 0;
    color: var(--black);
}

.auth-subtitle {
    margin: 0 0 24px 0;
    color: var(--dark-grey);
    font-size: 14px;
}

.auth-error {
    background: #fef2f2;
    color: var(--red);
    padding: 10px 14px;
    border-radius: 6px;
    margin-bottom: 16px;
    font-size: 14px;
}

.form-group {
    margin-bottom: 16px;
    text-align: left;
}

.form-group label {
    display: block;
    margin-bottom: 4px;
    font-size: 14px;
    font-weight: 500;
    color: #333;
}

.form-group input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid var(--light-grey);
    border-radius: 6px;
    font-size: 14px;
    box-sizing: border-box;
}

.form-group input:focus {
    outline: none;
    border-color: var(--green);
    box-shadow: 0 0 0 2px rgba(17, 140, 24, 0.15);
}

.auth-btn {
    width: 100%;
    padding: 12px;
    background: var(--green);
    color: white;
    border: none;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;
}

.auth-btn:hover:not(:disabled) {
    background: var(--dark-green);
}

.auth-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}
</style>
